import { inferFormation, type FormationPrediction } from './formation';
import type {
  BanRecommendation, DataSnapshot, DraftState, Hero, HeroMeta, LaneAnalysis, MatchupDatum,
  PickRecommendation, Position, RankTier, Recommendation,
} from '../types';

const NEUTRAL = 50;
const POSITIONS: Position[] = [1, 2, 3, 4, 5];
export const OPPOSING_POSITIONS: Record<Position, Position[]> = { 1: [3, 4], 2: [2], 3: [1, 5], 4: [1, 5], 5: [3, 4] };

function clamp(value: number, min = 0, max = 100): number {
  return Math.min(max, Math.max(min, value));
}

function percentile(values: Array<{ id: number; value: number | null }>): Map<number, number> {
  const known = values.filter((item): item is { id: number; value: number } => item.value !== null && Number.isFinite(item.value));
  const sorted = [...known].sort((a, b) => a.value - b.value || a.id - b.id);
  const result = new Map<number, number>();
  if (sorted.length === 1) result.set(sorted[0].id, 100);
  sorted.forEach((item, index) => result.set(item.id, sorted.length === 1 ? 100 : (index / (sorted.length - 1)) * 100));
  values.forEach((item) => { if (!result.has(item.id)) result.set(item.id, NEUTRAL); });
  return result;
}

function metaThreat(meta: HeroMeta | undefined): number | null {
  if (!meta) return null;
  return (meta.winRate - 50) * 2.5 + meta.pickRate * 0.9 + meta.banRate * 0.45;
}

function getRankMeta(snapshot: DataSnapshot, tier: RankTier, heroId: number): HeroMeta | undefined {
  if (tier !== 'all') return snapshot.rankMeta[tier]?.[String(heroId)];
  const rows = Object.entries(snapshot.rankMeta)
    .filter(([key]) => key !== 'all')
    .map(([, group]) => group?.[String(heroId)])
    .filter((item): item is HeroMeta => Boolean(item));
  if (!rows.length) return snapshot.rankMeta.all?.[String(heroId)];
  return {
    winRate: rows.reduce((sum, row) => sum + row.winRate, 0) / rows.length,
    pickRate: rows.reduce((sum, row) => sum + row.pickRate, 0) / rows.length,
    banRate: rows.reduce((sum, row) => sum + row.banRate, 0) / rows.length,
  };
}

function indexMatchups(matchups: MatchupDatum[]): Map<string, MatchupDatum> {
  return new Map(matchups.map((row) => [`${row.heroId}:${row.otherHeroId}`, row]));
}

function lookupPair(matchups: ReadonlyMap<string, MatchupDatum>, heroId: number, otherHeroId: number): MatchupDatum | undefined {
  return matchups.get(`${heroId}:${otherHeroId}`);
}

function meanKnown(values: Array<number | null | undefined>): number | null {
  const known = values.filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  return known.length ? known.reduce((sum, value) => sum + value, 0) / known.length : null;
}

function positionFit(hero: Hero, position: Position, meta?: HeroMeta): number {
  const explicit = meta?.positionScore?.[position];
  if (typeof explicit === 'number') return clamp(explicit);
  if (hero.positionRates) {
    const peak = Math.max(...POSITIONS.map((p) => hero.positionRates[p] || 0), 1);
    return clamp((hero.positionRates[position] / peak) * 100);
  }
  const index = hero.positions.indexOf(position);
  return index < 0 ? 0 : clamp(100 - index * 12);
}

function availableHeroes(snapshot: DataSnapshot, state: DraftState, kind: 'pick' | 'ban'): Hero[] {
  const unavailable = new Set([...state.allies, ...state.enemies, ...state.bans]);
  return snapshot.heroes.filter((hero) => {
    if (unavailable.has(hero.id)) return false;
    if (kind === 'pick' && !hero.positions.includes(state.targetPosition)) return false;
    if (kind === 'pick' && state.poolOnly && !state.personalPool.includes(hero.id)) return false;
    return true;
  });
}

interface RawLaneContext {
  raw: number | null;
  confidence: number;
  coverage: number;
  candidatePosition: Position;
  opponents: LaneAnalysis['opponents'];
  source: LaneAnalysis['source'];
}

function laneRelation(snapshot: DataSnapshot, matchups: ReadonlyMap<string, MatchupDatum>, heroId: number, otherHeroId: number, position: Position, otherPosition: Position): { value: number | null; source: LaneAnalysis['source'] } {
  const exact = snapshot.laneMatchups?.find((row) => row.heroId === heroId && row.otherHeroId === otherHeroId && row.position === position && row.otherPosition === otherPosition);
  if (exact) return { value: exact.advantage, source: 'laneMatchups' };
  const reverse = snapshot.laneMatchups?.find((row) => row.heroId === otherHeroId && row.otherHeroId === heroId && row.position === otherPosition && row.otherPosition === position);
  if (reverse) return { value: -reverse.advantage, source: 'laneMatchups' };
  const overall = lookupPair(matchups, heroId, otherHeroId)?.counter;
  if (typeof overall === 'number') return { value: overall, source: 'matchups' };
  const reverseOverall = lookupPair(matchups, otherHeroId, heroId)?.counter;
  return { value: typeof reverseOverall === 'number' ? -reverseOverall : null, source: 'matchups' };
}

function rawLaneContext(snapshot: DataSnapshot, matchups: ReadonlyMap<string, MatchupDatum>, candidate: Hero, candidatePrediction: FormationPrediction, opposingPredictions: FormationPrediction[]): RawLaneContext {
  const opponentIds = new Set(OPPOSING_POSITIONS[candidatePrediction.position]);
  const encounters = opposingPredictions.filter((prediction) => opponentIds.has(prediction.position));
  const heroById = new Map(snapshot.heroes.map((hero) => [hero.id, hero]));
  const relations = encounters.map((opponent) => laneRelation(snapshot, matchups, candidate.id, opponent.heroId, candidatePrediction.position, opponent.position));
  const known = relations.filter((relation) => relation.value !== null);
  const confidence = encounters.length
    ? (candidatePrediction.confidence / 100) * (encounters.reduce((sum, opponent) => sum + opponent.confidence, 0) / encounters.length / 100)
    : 0;
  const sources = new Set(known.map((relation) => relation.source));
  return {
    raw: meanKnown(relations.map((relation) => relation.value)),
    confidence,
    coverage: encounters.length ? known.length / encounters.length : 0,
    candidatePosition: candidatePrediction.position,
    opponents: encounters.map((opponent) => ({ heroId: opponent.heroId, heroName: heroById.get(opponent.heroId)?.nameZh ?? String(opponent.heroId), position: opponent.position })),
    source: sources.size > 1 ? 'mixed' : sources.has('laneMatchups') ? 'laneMatchups' : sources.has('matchups') ? 'matchups' : 'none',
  };
}

function laneAnalysis(context: RawLaneContext, percentileScore: number): LaneAnalysis {
  const effective = context.raw === null || !context.opponents.length
    ? NEUTRAL
    : NEUTRAL + (percentileScore - NEUTRAL) * context.confidence * context.coverage;
  return {
    score: Number(effective.toFixed(1)),
    rawScore: Number((context.raw === null ? NEUTRAL : percentileScore).toFixed(1)),
    confidence: Math.round(context.confidence * 100),
    coverage: Math.round(context.coverage * 100),
    source: context.source,
    candidatePosition: context.candidatePosition,
    opponents: context.opponents,
    explanation: context.opponents.length
      ? `预计对阵${context.opponents.map((opponent) => `${opponent.heroName} P${opponent.position}`).join('、')}`
      : '当前没有可推断的对位英雄',
  };
}

function teamHeroes(snapshot: DataSnapshot, ids: number[]): Hero[] {
  const byId = new Map(snapshot.heroes.map((hero) => [hero.id, hero]));
  return ids.map((id) => byId.get(id)).filter((hero): hero is Hero => Boolean(hero));
}

export function recommend(snapshot: DataSnapshot, state: DraftState, kind: 'pick' | 'ban' = 'pick', limit = 12): Recommendation[] {
  if (kind === 'ban' && state.mode !== 'league') return [];
  const heroes = availableHeroes(snapshot, state, kind);
  const matchups = indexMatchups(state.mode === 'league' ? snapshot.leagueMatchups : snapshot.matchups);
  const allies = teamHeroes(snapshot, state.allies);
  const enemies = teamHeroes(snapshot, state.enemies);
  const allyFormation = inferFormation(allies, state.positionOverrides.ally);
  const enemyFormation = inferFormation(enemies, state.positionOverrides.enemy);

  const raw = heroes.map((hero) => {
    const meta = state.mode === 'league' ? snapshot.leagueMeta[String(hero.id)] : getRankMeta(snapshot, state.tier, hero.id);
    const synergy = meanKnown(state.allies.map((id) => lookupPair(matchups, hero.id, id)?.synergy));
    const counter = meanKnown((kind === 'ban' ? state.allies : state.enemies).map((id) => lookupPair(matchups, hero.id, id)?.counter));
    const flex = hero.positions.length * 18 + (meta?.pickRate ?? 0);
    const candidateFormation = kind === 'pick'
      ? inferFormation([...allies, hero], { ...state.positionOverrides.ally, [hero.id]: state.targetPosition })
      : inferFormation([...enemies.slice(0, 4), hero], state.positionOverrides.enemy);
    const candidatePrediction = candidateFormation.predictions.find((prediction) => prediction.heroId === hero.id) ?? {
      heroId: hero.id,
      position: kind === 'pick' ? state.targetPosition : hero.positions[0] ?? 1,
      confidence: 0,
      probabilities: hero.positionRates,
      manual: kind === 'pick',
    };
    return {
      hero,
      rawMeta: metaThreat(meta),
      synergy,
      counter,
      position: positionFit(hero, state.targetPosition, meta),
      flex,
      lane: rawLaneContext(snapshot, matchups, hero, candidatePrediction, kind === 'pick' ? enemyFormation.predictions : allyFormation.predictions),
    };
  });

  const metaScores = percentile(raw.map((row) => ({ id: row.hero.id, value: row.rawMeta })));
  const synergyScores = percentile(raw.map((row) => ({ id: row.hero.id, value: row.synergy })));
  const counterScores = percentile(raw.map((row) => ({ id: row.hero.id, value: row.counter })));
  const flexScores = percentile(raw.map((row) => ({ id: row.hero.id, value: row.flex })));
  const laneScores = percentile(raw.map((row) => ({ id: row.hero.id, value: row.lane.raw })));

  return raw.map((row): Recommendation => {
    const meta = metaScores.get(row.hero.id) ?? NEUTRAL;
    const synergy = synergyScores.get(row.hero.id) ?? NEUTRAL;
    const counter = counterScores.get(row.hero.id) ?? NEUTRAL;
    const lane = laneAnalysis(row.lane, laneScores.get(row.hero.id) ?? NEUTRAL);
    if (kind === 'ban') {
      const flexibility = flexScores.get(row.hero.id) ?? NEUTRAL;
      const score = lane.score * 0.35 + meta * 0.3 + counter * 0.25 + flexibility * 0.1;
      const recommendation: BanRecommendation = {
        hero: row.hero, kind, score: Number(score.toFixed(1)),
        breakdown: { lane: Math.round(lane.score), meta: Math.round(meta), counter: Math.round(counter), flexibility: Math.round(flexibility) },
        coverage: Math.round(([row.rawMeta, state.allies.length ? row.counter : null, row.flex, row.lane.raw].filter((value) => value !== null).length / 4) * 100),
        reasons: buildReasons({ lane: lane.score, meta, counter, flexibility }, kind), laneAnalysis: lane,
      };
      return recommendation;
    }
    const position = row.position;
    const score = lane.score * 0.35 + meta * 0.2 + counter * 0.15 + synergy * 0.15 + position * 0.15;
    const recommendation: PickRecommendation = {
      hero: row.hero, kind, score: Number(score.toFixed(1)),
      breakdown: { lane: Math.round(lane.score), meta: Math.round(meta), counter: Math.round(counter), synergy: Math.round(synergy), position: Math.round(position) },
      coverage: Math.round(([row.rawMeta, state.enemies.length ? row.counter : null, state.allies.length ? row.synergy : null, row.position, row.lane.raw].filter((value) => value !== null).length / 5) * 100),
      reasons: buildReasons({ lane: lane.score, meta, counter, synergy, position }, kind, state.targetPosition), laneAnalysis: lane,
    };
    return recommendation;
  }).sort((a, b) => b.score - a.score || a.hero.id - b.hero.id).slice(0, limit);
}

function buildReasons(scores: Record<string, number>, kind: 'pick' | 'ban', targetPosition?: Position): string[] {
  const labels = kind === 'ban'
    ? [{ key: 'lane', text: '对线威胁较高' }, { key: 'meta', text: '联赛环境强势' }, { key: 'counter', text: '对己方整体克制' }, { key: 'flexibility', text: '位置摇摆空间大' }]
    : [{ key: 'lane', text: '对线占优' }, { key: 'counter', text: '克制敌方阵容' }, { key: 'synergy', text: '与己方阵容协同' }, { key: 'meta', text: '当前环境表现稳定' }, { key: 'position', text: `${targetPosition} 号位适配度高` }];
  return labels.sort((a, b) => scores[b.key] - scores[a.key]).slice(0, 2).map((item) => `${item.text} · ${Math.round(scores[item.key])}`);
}

export const recommendationInternals = { percentile, getRankMeta, positionFit, laneRelation, rawLaneContext, laneAnalysis };
